/**
 * Deposit Manager - Event-driven deposit product management
 *
 * This module listens to cart events and automatically manages deposit products
 * based on the current marketplace. It handles:
 * - Adding deposits when main products are added
 * - Updating deposit quantities when main product quantities change
 * - Removing deposits when main products are removed
 * - Re-adding deposits if user tries to remove them directly
 * - Reconciling deposits when marketplace changes
 *
 * No modifications to existing cart JS files required - this is purely event-driven.
 *
 * Saturo: adapted to the Horizon 4.1.1 event system. The 3.2.1
 * 'cart:update' CustomEvent (detail.resource = raw cart JSON) no longer
 * exists; 4.1.1 components communicate via the Shopify standard event
 * StandardEvents.cartLinesUpdate ('shopify:cart:lines-update') from
 * '@shopify/events' (importmap -> cdn.shopify.com/storefront/standard-events.js).
 * Handlers receive the payload asynchronously via
 * event.promise.then(({ cart, detail }) => ...) where detail.items is the
 * full raw /cart.js items list. This file is therefore loaded as a module
 * (see layout/theme.liquid) so it can import the event class and dispatch
 * updates the same way the theme's own components do.
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

(function() {
  'use strict';

  // Ensure config exists
  if (!window.DepositConfig) {
    console.warn('DepositManager: DepositConfig not found. Deposit management disabled.');
    return;
  }

  const config = window.DepositConfig;
  
  // State management (theme 5.91: one serialized runner)
  //
  // Exactly one reconcile run (fetch -> analyze -> write) executes at a
  // time. Requests that arrive while a run is in flight set rerunRequested
  // and are replayed ONCE from a fresh /cart.js fetch after the run - a
  // payload captured while the cart is being mutated is stale by
  // definition. Requests that arrive while only the debounce timer is
  // pending are coalesced; a request for a fresh fetch is sticky, later
  // hints cannot downgrade it.
  let inFlight = false;
  let pendingTimer = null;
  let pendingHint = null;
  let pendingWantsFetch = false;
  let rerunRequested = false;
  let retryArmed = false;
  let bootIdleHandle = null;
  // Any cart event or bfcache restore seen in this document: the page-load
  // snapshot is no longer trustworthy after that.
  let cartActivity = false;
  let cachedDomDepositInfo = null;

  /**
   * Debounce utility to prevent rapid successive calls
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Fetch current cart state from Shopify
   */
  async function fetchCart() {
    try {
      const response = await fetch(config.routes.cartGet, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        console.warn('DepositManager: cart fetch failed', response.status);
        return null;
      }
      const cart = await response.json();
      // A JSON error envelope ({status, message}) or a challenge page must
      // never reach analyzeDeposits.
      if (!cart || !Array.isArray(cart.items)) return null;
      return cart;
    } catch (error) {
      console.error('DepositManager: Failed to fetch cart', error);
      return null;
    }
  }

  /**
   * Saturo: collect the section ids of every cart-items-component on the page,
   * mirroring component-cart-items.js. Passing them as the `sections` param of
   * a cart mutation makes Shopify return the re-rendered section HTML, which
   * 4.1.1 listeners morph in place (detail.sections -> morphSection) instead
   * of refetching.
   */
  function getCartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
    return Array.from(ids);
  }

  /**
   * Update cart with multiple items at once
   */
  async function updateCart(updates, properties = {}) {
    try {
      const body = { updates };

      // Saturo: request re-rendered cart sections along with the mutation (4.1.1)
      const sectionIds = getCartSectionIds();
      if (sectionIds.length > 0) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }

      let response = await fetch(config.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      // Shopify answers overlapping Cart Ajax writes with 409; retry once
      // after a short pause (same policy as the cart-offer engine).
      if (response.status === 409) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        response = await fetch(config.routes.cartUpdate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      }

      const result = await response.json();

      if (!response.ok || result.errors || (result.status && result.status !== 200)) {
        console.error('DepositManager: Cart update error', result.errors || result.message || response.status);
        return null;
      }

      return result;
    } catch (error) {
      console.error('DepositManager: Failed to update cart', error);
      return null;
    }
  }

  /**
   * Add items to cart with properties
   */
  async function addToCart(items) {
    try {
      const body = { items };

      // Saturo: request re-rendered cart sections along with the mutation (4.1.1)
      const sectionIds = getCartSectionIds();
      if (sectionIds.length > 0) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(config.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      const result = await response.json();
      
      if (result.status && result.status !== 200) {
        console.error('DepositManager: Cart add error', result.message);
        return null;
      }
      
      return result;
    } catch (error) {
      console.error('DepositManager: Failed to add to cart', error);
      return null;
    }
  }

  /**
   * Check if a product type indicates it's a deposit product
   */
  function isDepositProductType(productType) {
    if (!productType) return false;
    return productType.toLowerCase().startsWith(config.depositProductTypePrefix);
  }

  /**
   * Extract market code from deposit product type (e.g., "deposit-de" -> "de")
   */
  function getMarketFromDepositType(productType) {
    if (!productType) return null;
    const lower = productType.toLowerCase();
    if (lower.startsWith(config.depositProductTypePrefix)) {
      return lower.substring(config.depositProductTypePrefix.length);
    }
    return null;
  }

  /**
   * Check if a cart item is a deposit product: by the `_is_deposit` property
   * (lines this file or the checkout extension wrote), by the `deposit-{iso}`
   * product type, or (5.153) by its variant being a deposit variant the page's
   * lookup data points to. The last one catches deposit lines that lost their
   * properties ("Order again" in the customer account, draft orders,
   * permalinks) even when the deposit product's type is not set: such a line
   * was otherwise treated as an unknown main product and never cleaned up.
   */
  function isDepositItem(item) {
    return isDepositProductType(item.product_type) ||
           (item.properties && item.properties._is_deposit === 'true') ||
           getKnownDepositVariantIds().has(String(item.variant_id));
  }

  /**
   * Every deposit variant the three lookup sources (variantDepositInfo, DOM
   * quick-add payloads, cartDepositInfo) point to. Cached with the DOM cache.
   */
  let cachedDepositVariantIds = null;

  function getKnownDepositVariantIds() {
    if (cachedDepositVariantIds !== null) return cachedDepositVariantIds;
    const ids = new Set();
    const add = (info) => {
      if (info && info.depositVariantId) ids.add(String(info.depositVariantId));
    };
    Object.values(config.variantDepositInfo || {}).forEach(add);
    Object.values(collectDepositInfoFromDOM()).forEach(add);
    (config.cartDepositInfo || []).forEach(add);
    cachedDepositVariantIds = ids;
    return ids;
  }

  /**
   * Check if a cart item is a free-gift line added by the cart-offer engine
   * (`_offer_id`). Since 5.152 gifts carry Pfand like any other line (a free
   * container still carries a deposit; the gift's price is zeroed by the app's
   * pricing Function, the deposit line is charged normally). The checkout
   * extension (cart-checkout-extension v92) counts gifts the same way - both
   * sides must agree or they correct each other's deposit line. Only used to
   * keep an engine-added gift WITHOUT lookup data from blocking quantity cuts
   * (see analyzeDeposits).
   */
  function isOfferGiftItem(item) {
    return !!(item.properties && item.properties._offer_id);
  }

  /**
   * Check if a cart item is a main product (anything that is not a deposit,
   * gifts included since 5.152).
   */
  function isMainProduct(item) {
    return !isDepositItem(item);
  }

  /**
   * Collect deposit info from quick-add components on the page
   * This captures deposit data for products displayed in collections/grids
   * Results are cached to avoid repeated DOM queries during a single analysis
   */
  function collectDepositInfoFromDOM(useCache = true) {
    // Return cached result if available
    if (useCache && cachedDomDepositInfo !== null) {
      return cachedDomDepositInfo;
    }
    
    const domDepositInfo = {};
    
    // Find all quick-add components with deposit data
    const quickAddComponents = document.querySelectorAll('quick-add-component[data-variant-deposits]');
    
    for (const component of quickAddComponents) {
      try {
        const depositsJson = component.getAttribute('data-variant-deposits');
        if (depositsJson) {
          const deposits = JSON.parse(depositsJson);
          Object.assign(domDepositInfo, deposits);
        }
      } catch (e) {
        console.warn('DepositManager: Failed to parse deposit data from DOM', e);
      }
    }
    
    // Cache the result
    cachedDomDepositInfo = domDepositInfo;
    return domDepositInfo;
  }
  
  /**
   * Clear the DOM deposit info cache (call before each analysis)
   */
  function clearDomCache() {
    cachedDomDepositInfo = null;
    cachedDomVariantIds = null;
    cachedDepositVariantIds = null;
  }

  /**
   * Is this variant known to the page at all (with or without a deposit)?
   * Sources: variants of the current product (config.knownVariantIds), lines
   * that were in the cart when the page rendered (config.cartItems), and the
   * variants of every quick-add card on the page (data-variant-ids). A
   * variant in none of them entered the cart from another page or tab, so
   * this page cannot tell whether it needs a deposit.
   */
  let cachedDomVariantIds = null;

  // 5.153: cart variants learned from sections/deposit-cart-data.liquid after
  // page load, and the variants a lookup was already attempted for (one
  // request per unknown variant per page, never a loop).
  const refreshedKnownVariantIds = new Set();
  const lookupAttemptedVariantIds = new Set();

  /**
   * Fetch deposit lookup data for the CURRENT cart (Section Rendering API,
   * sections/deposit-cart-data.liquid) when lines are unknown to this page,
   * so deposit cuts and orphan removals do not wait for the next page load.
   * Merges the fresh cartDepositInfo into config and marks every cart variant
   * as known. Returns true when new data was merged.
   */
  async function refreshCartLookup(unknownVariantIds) {
    const pending = unknownVariantIds.filter((id) => !lookupAttemptedVariantIds.has(id));
    if (pending.length === 0) return false;
    pending.forEach((id) => lookupAttemptedVariantIds.add(id));
    try {
      const root = config.rootUrl || '/';
      const url = `${root.endsWith('/') ? root : root + '/'}?sections=deposit-cart-data`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return false;
      const html = (await response.json())['deposit-cart-data'];
      if (!html) return false;
      const script = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('script[data-deposit-cart-data]');
      if (!script) return false;
      const data = JSON.parse(script.textContent);
      // A market switch in between makes the data useless for this page.
      if (data.market !== config.currentMarket) return false;
      const fresh = Array.isArray(data.cartDepositInfo) ? data.cartDepositInfo : [];
      const freshIds = new Set(fresh.map((info) => String(info.mainVariantId)));
      config.cartDepositInfo = fresh.concat(
        (config.cartDepositInfo || []).filter((info) => !freshIds.has(String(info.mainVariantId)))
      );
      (data.variantIds || []).forEach((id) => refreshedKnownVariantIds.add(String(id)));
      return true;
    } catch (error) {
      console.warn('DepositManager: cart lookup refresh failed', error);
      return false;
    }
  }

  function isKnownVariant(variantId) {
    const id = String(variantId);
    if (Array.isArray(config.knownVariantIds) && config.knownVariantIds.some((v) => String(v) === id)) return true;
    if (Array.isArray(config.cartItems) && config.cartItems.some((item) => String(item.variant_id) === id)) return true;
    if (refreshedKnownVariantIds.has(id)) return true;
    if (cachedDomVariantIds === null) {
      cachedDomVariantIds = new Set();
      document.querySelectorAll('[data-variant-ids]').forEach((el) => {
        String(el.getAttribute('data-variant-ids') || '').split(',').forEach((v) => {
          if (v.trim()) cachedDomVariantIds.add(v.trim());
        });
      });
    }
    return cachedDomVariantIds.has(id);
  }

  /**
   * Deposit units per main unit, normalized like the checkout extension
   * (cart-checkout-extension `buildDepositRules`): integers >= 1 are kept,
   * anything else (0, negative, blank, junk) counts as 1. "No deposit" is
   * expressed by clearing the deposit_{iso} reference, never by a 0 count.
   * Liquid's `default: 1` does not replace 0, so all three lookup paths
   * normalize here (5.152; the cart path used to pass a raw 0 through).
   */
  function normalizeNumberOfItems(value) {
    const n = Number.parseInt(value, 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }

  /**
   * Find deposit info for a variant from the pre-computed config
   * Checks:
   * 1. variantDepositInfo - variants from products on the current page (for new adds)
   * 2. DOM data attributes - deposit info from quick-add components in collections
   * 3. cartDepositInfo - variants already in the cart
   */
  function getDepositInfoForVariant(variantId) {
    const variantIdStr = variantId.toString();
    
    // First check variantDepositInfo (products on current page - product pages)
    // This is an object keyed by variant ID
    if (config.variantDepositInfo && config.variantDepositInfo[variantIdStr]) {
      const info = config.variantDepositInfo[variantIdStr];
      return {
        mainVariantId: variantIdStr,
        depositVariantId: info.depositVariantId,
        depositProductId: info.depositProductId,
        depositProductHandle: info.depositProductHandle,
        numberOfItems: normalizeNumberOfItems(info.numberOfItems),
        depositProductType: info.depositProductType
      };
    }
    
    // Then check DOM for deposit info (quick-add components in collections)
    const domDepositInfo = collectDepositInfoFromDOM();
    if (domDepositInfo[variantIdStr]) {
      const info = domDepositInfo[variantIdStr];
      return {
        mainVariantId: variantIdStr,
        depositVariantId: info.depositVariantId,
        numberOfItems: normalizeNumberOfItems(info.numberOfItems)
      };
    }
    
    // Finally check cartDepositInfo (items already in cart)
    // This is an array
    if (config.cartDepositInfo) {
      const found = config.cartDepositInfo.find(info => 
        info.mainVariantId.toString() === variantIdStr
      );
      if (found) {
        return { ...found, numberOfItems: normalizeNumberOfItems(found.numberOfItems) };
      }
    }
    
    return null;
  }

  /**
   * Analyze current cart state and determine what deposit changes are needed
   * Deposits are CONSOLIDATED by deposit variant ID - one line item per deposit product
   */
  function analyzeDeposits(cart) {
    // Clear DOM cache for fresh data
    clearDomCache();
    
    const currentMarket = config.currentMarket;
    const toAdd = [];
    const toUpdate = [];
    const toRemove = [];

    // Build maps for easier lookup
    // mainProductsMap aggregates quantities for the same variant across multiple line items
    // (e.g., same variant with different selling plans creates separate line items)
    const mainProductsMap = new Map(); // variantId -> { totalQuantity, items[] }
    // depositVariantId -> cart items. A list (5.153): the same deposit variant
    // can sit on several lines (a tagged line next to an untagged one from
    // "Order again"); they are consolidated into one below.
    const depositsByVariantId = new Map();
    // Saturo (theme 5.91): a main product this page knows nothing about (not
    // in the current product's variants, not in the cart at render time, not
    // on a quick-add card): it entered the cart on another page or tab and
    // may legitimately own a deposit line. Removals and quantity cuts are
    // skipped for the whole pass then - only additions and increases are
    // safe with incomplete knowledge. Known products WITHOUT a deposit
    // (powders, shakers, boxes) do not trigger this.
    let unknownMainProduct = false;
    const unknownVariantIds = []; // 5.153: fed to refreshCartLookup

    for (const item of cart.items) {
      if (isDepositItem(item)) {
        // Track deposits by their variant ID (for consolidation)
        const depositVariantIdStr = item.variant_id.toString();
        if (depositsByVariantId.has(depositVariantIdStr)) {
          depositsByVariantId.get(depositVariantIdStr).push(item);
        } else {
          depositsByVariantId.set(depositVariantIdStr, [item]);
        }
      } else {
        // Main products INCLUDING cart-offer gift lines (`_offer_id`, 5.152):
        // gifted units consolidate into the same deposit line as paid ones.
        const variantIdStr = item.variant_id.toString();
        if (mainProductsMap.has(variantIdStr)) {
          // Same variant exists (possibly with different selling plan) - aggregate quantity
          const existing = mainProductsMap.get(variantIdStr);
          existing.totalQuantity += item.quantity;
          existing.items.push(item);
        } else {
          mainProductsMap.set(variantIdStr, {
            totalQuantity: item.quantity,
            items: [item]
          });
        }
      }
    }

    // Calculate TOTAL expected quantity for each deposit variant
    // (consolidated across all main products that need this deposit)
    const expectedDepositTotals = new Map(); // depositVariantId -> { total, mainVariantIds }

    for (const [variantId, variantData] of mainProductsMap) {
      const depositInfo = getDepositInfoForVariant(variantId);

      if (!depositInfo) {
        // A line whose product is not on this page has no lookup data from the
        // page render. runScheduledSync fetches it (refreshCartLookup, 5.153)
        // and analyzes again. An engine-added gift must not block quantity
        // cuts for the whole cart (unknownMainProduct) in the meantime; the
        // checkout extension adds its deposit regardless.
        const onlyGifts = variantData.items.every(isOfferGiftItem);
        if (!isKnownVariant(variantId)) {
          // Gifts only feed the lookup refresh (5.153: their deposit no longer
          // waits for the next page); they never block cuts.
          if (!onlyGifts) unknownMainProduct = true;
          unknownVariantIds.push(variantId);
        }
        continue;
      }

      if (depositInfo) {
        const depositVariantId = depositInfo.depositVariantId.toString();
        // Use aggregated totalQuantity instead of single item.quantity
        const neededQty = variantData.totalQuantity * depositInfo.numberOfItems;
        
        if (expectedDepositTotals.has(depositVariantId)) {
          const existing = expectedDepositTotals.get(depositVariantId);
          existing.total += neededQty;
          existing.mainVariantIds.push(variantId);
        } else {
          expectedDepositTotals.set(depositVariantId, {
            total: neededQty,
            mainVariantIds: [variantId],
            depositInfo: depositInfo
          });
        }
      }
    }

    // Compare expected totals with actual deposits in cart
    for (const [depositVariantId, expected] of expectedDepositTotals) {
      const existingDeposits = depositsByVariantId.get(depositVariantId);

      if (existingDeposits) {
        // Deposit exists - consolidate onto one line (5.153): keep the tagged
        // line if there is one (an update cannot add properties), remove the
        // others. Never cut the total while a main product is unknown on this
        // page: the keeper then takes over the extras' quantity instead.
        const keeper = existingDeposits.find(
          (item) => item.properties && item.properties._is_deposit === 'true'
        ) || existingDeposits[0];
        const actualTotal = existingDeposits.reduce((sum, item) => sum + item.quantity, 0);
        const targetTotal = unknownMainProduct && expected.total < actualTotal
          ? actualTotal
          : expected.total;

        for (const extra of existingDeposits) {
          if (extra === keeper) continue;
          toRemove.push({
            key: extra.key,
            variantId: extra.variant_id,
            quantity: 0,
            reason: 'duplicate_deposit_line'
          });
        }
        if (keeper.quantity !== targetTotal) {
          toUpdate.push({
            key: keeper.key,
            variantId: parseInt(depositVariantId),
            quantity: targetTotal,
            mainVariantIds: expected.mainVariantIds,
            reason: 'quantity_mismatch'
          });
        }
      } else {
        // No deposit exists - need to add one (consolidated)
        toAdd.push({
          variantId: parseInt(depositVariantId),
          quantity: expected.total,
          mainVariantIds: expected.mainVariantIds,
          properties: {
            _is_deposit: 'true',
            _deposit_market: currentMarket,
            _deposit_for_variants: JSON.stringify(expected.mainVariantIds)
          }
        });
      }
    }

    // Check for orphaned deposits (no main products need this deposit anymore).
    // Skipped while a main product is unknown on this page: the deposit may
    // belong to it.
    for (const [depositVariantId, depositItems] of depositsByVariantId) {
      if (!unknownMainProduct && !expectedDepositTotals.has(depositVariantId)) {
        // No main products need this deposit - remove all its lines
        for (const depositItem of depositItems) {
          toRemove.push({
            key: depositItem.key,
            variantId: depositItem.variant_id,
            quantity: 0,
            reason: 'no_main_products_need_deposit'
          });
        }
      }
    }

    // Check for deposits from wrong market
    for (const item of cart.items) {
      if (isDepositItem(item)) {
        const depositMarket = item.properties?._deposit_market || 
                              getMarketFromDepositType(item.product_type);
        
        if (depositMarket && depositMarket !== currentMarket) {
          // This deposit is from a different market - remove it
          // (correct market deposit will be added by the logic above if needed)
          if (!toRemove.find(r => r.key === item.key)) {
            toRemove.push({
              key: item.key,
              variantId: item.variant_id,
              quantity: 0,
              reason: 'wrong_market'
            });
          }
        }
      }
    }

    // (The former "re-add protection" block was removed in theme 5.91: the
    // expected-vs-actual comparison above already adds every missing
    // deposit, including one the customer deleted.)

    return { toAdd, toUpdate, toRemove, unknownVariantIds };
  }

  /**
   * Sync deposits based on analysis
   */
  async function syncDeposits(toAdd, toUpdate, toRemove) {
    if (toAdd.length === 0 && toUpdate.length === 0 && toRemove.length === 0) {
      return; // Nothing to do
    }

    try {
      // Saturo: keep the section HTML of the LAST mutation - it reflects the
      // final cart state and lets 4.1.1 listeners morph instead of refetch.
      let sections = null;

      // Handle removals and updates first via /cart/update.js, keyed by line
      // key (5.153): a variant id addresses only one of several lines of the
      // same deposit variant, so duplicates could never be removed.
      const updates = {};

      for (const item of toRemove) {
        updates[item.key || item.variantId] = 0;
      }

      for (const item of toUpdate) {
        updates[item.key || item.variantId] = item.quantity;
      }

      if (Object.keys(updates).length > 0) {
        const result = await updateCart(updates);
        if (result && result.sections) sections = result.sections;
      }

      // Handle additions via /cart/add.js (to include properties)
      if (toAdd.length > 0) {
        const items = toAdd.map(item => ({
          id: item.variantId,
          quantity: item.quantity,
          properties: item.properties
        }));

        const result = await addToCart(items);
        if (result && result.sections) sections = result.sections;
      }

      // Fetch updated cart to get correct counts
      const updatedCart = await fetchCart();

      // Calculate item count EXCLUDING deposit products
      let adjustedItemCount = 0;
      if (updatedCart && updatedCart.items) {
        for (const item of updatedCart.items) {
          if (!isDepositItem(item)) {
            adjustedItemCount += item.quantity;
          }
        }
      }

      // Saturo: notify the 4.1.1 components the same way the theme does -
      // dispatch the standard cart-lines-update event carrying an (already
      // resolved) promise. Listeners (cart-items-component, cart-icon,
      // cart-drawer, ...) read { cart, detail } from event.promise; when
      // detail.sections is present they morph, otherwise they refetch via
      // the section renderer. action is 'update' (never 'add') so the cart
      // drawer's auto-open-on-add behavior is not triggered by deposit sync.
      if (updatedCart) {
        const deposit_lines = (updatedCart.items || [])
          .filter((item) => isDepositItem(item))
          .map((item) => ({ id: item.key, quantity: item.quantity }));

        const deferred = CartLinesUpdateEvent.createPromise();
        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'update',
            context: 'cart',
            lines: deposit_lines,
            promise: deferred.promise,
          })
        );
        // Saturo (5.180): another cart event resolved during this run (it set
        // rerunRequested), so our section HTML may predate that mutation and
        // would be morphed over its newer render - let listeners refetch.
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
          detail: {
            sections: (!rerunRequested && sections) || undefined,
            items: updatedCart.items,
            itemCount: adjustedItemCount,
            source: 'deposit-manager',
            didError: false,
          },
        });
      }

    } catch (error) {
      console.error('DepositManager: Error syncing deposits', error);
    }
  }

  /**
   * Request a deposit reconciliation.
   *
   * @param {Object|null} cartHint - /cart.js-shaped cart ({items}) already
   *   known from an event payload; null or an empty items list means
   *   "fetch fresh". A hint flagged `fromSnapshot: true` (the server-rendered
   *   page-load snapshot) is only trusted for a "nothing to do" verdict -
   *   before any write the cart is re-fetched and re-analyzed, because the
   *   snapshot may predate a mutation that was still in flight when the
   *   page was rendered.
   * @param {{immediate?: boolean}} [options] - immediate skips the debounce.
   */
  function scheduleSync(cartHint, options = {}) {
    const wantsFetch = !cartHint || !Array.isArray(cartHint.items) || cartHint.items.length === 0;

    if (inFlight) {
      rerunRequested = true;
      return;
    }

    if (pendingTimer) clearTimeout(pendingTimer);
    if (wantsFetch) {
      pendingWantsFetch = true;
      pendingHint = null;
    } else if (!pendingWantsFetch) {
      pendingHint = cartHint;
    }

    pendingTimer = setTimeout(runScheduledSync, options.immediate ? 0 : config.debounceMs);
  }

  function hasActions(actions) {
    return actions.toAdd.length > 0 || actions.toUpdate.length > 0 || actions.toRemove.length > 0;
  }

  async function runScheduledSync() {
    pendingTimer = null;
    const hint = pendingWantsFetch ? null : pendingHint;
    pendingHint = null;
    pendingWantsFetch = false;

    inFlight = true;
    try {
      let cart = hint;
      if (!cart) {
        cart = await fetchCart();
        if (!cart) {
          // One bounded retry so a queued rerun is not lost to a network blip.
          if (!retryArmed) {
            retryArmed = true;
            setTimeout(() => {
              retryArmed = false;
              scheduleSync(null);
            }, 1000);
          }
          return;
        }
      }

      let actions = analyzeDeposits(cart);

      if (hasActions(actions) && cart.fromSnapshot) {
        const fresh = await fetchCart();
        if (!fresh) return;
        cart = fresh;
        actions = analyzeDeposits(fresh);
      }

      // 5.153: a line unknown to this page held back cuts and removals; fetch
      // its lookup data once and decide again with full knowledge.
      if (actions.unknownVariantIds.length > 0 && await refreshCartLookup(actions.unknownVariantIds)) {
        if (cart.fromSnapshot) {
          const fresh = await fetchCart();
          if (!fresh) return;
          cart = fresh;
        }
        actions = analyzeDeposits(cart);
      }

      if (hasActions(actions)) {
        await syncDeposits(actions.toAdd, actions.toUpdate, actions.toRemove);
      }
    } finally {
      inFlight = false;
      if (rerunRequested) {
        rerunRequested = false;
        scheduleSync(null);
      }
    }
  }

  /**
   * Main handler for cart updates.
   *
   * Saturo: 4.1.1 - the standard cart-lines-update event delivers its payload
   * via event.promise ({ cart, detail }); detail.items is the full raw
   * /cart.js items list and detail.source identifies the dispatcher.
   * @param {CartLinesUpdateEvent} event
   */
  function handleCartLinesUpdate(event) {
    cartActivity = true;
    if (!event.promise) {
      // Dispatcher without payload promise - reconcile from a fresh fetch.
      scheduleSync(null);
      return;
    }

    event.promise
      .then((result) => {
        const detail = result && result.detail;

        // Ignore our own events to prevent loops
        if (detail?.source === 'deposit-manager') {
          return;
        }
        if (detail?.didError) {
          return;
        }

        const cartHint = Array.isArray(detail?.items) ? { items: detail.items } : null;
        scheduleSync(cartHint);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('DepositManager: cart update promise rejected', error);
        }
      });
  }

  /**
   * Handle marketplace changes
   * Called on page load to check if market changed since last visit
   */
  function checkMarketChange() {
    const storedMarket = sessionStorage.getItem('deposit_market');
    const currentMarket = config.currentMarket;

    // Store current market for next page load
    sessionStorage.setItem('deposit_market', currentMarket);

    if (storedMarket && storedMarket !== currentMarket) {
      console.log('DepositManager: Market changed from', storedMarket, 'to', currentMarket);
      return true;
    }
    return false;
  }

  /**
   * Initialize on page load
   *
   * Saturo (theme 5.91): the boot reconcile no longer fetches /cart.js on
   * every page view. An empty server-rendered cart is skipped outright
   * (unless the market changed). Otherwise the server-rendered snapshot
   * (config.cartItems) is analyzed; only if it shows something to do is the
   * cart fetched fresh and re-analyzed before any write (the snapshot may be
   * stale). The boot runs at idle time so it never lands in the tap-to-paint
   * window INP measures - except on the cart page and after a market change,
   * where checkout is one tap away and the reconcile runs immediately.
   */
  function isCartTemplate() {
    try {
      return typeof Theme !== 'undefined' && Theme.template && Theme.template.name === 'cart';
    } catch (e) {
      return false;
    }
  }

  function cancelBootIdle() {
    if (bootIdleHandle === null) return;
    if (typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(bootIdleHandle);
    } else {
      clearTimeout(bootIdleHandle);
    }
    bootIdleHandle = null;
  }

  function init() {
    const marketChanged = checkMarketChange();
    if (!marketChanged && config.cartItemCount === 0) return;

    const run = () => {
      bootIdleHandle = null;
      // A cart event (or a bfcache restore) already happened in this document:
      // the page-load snapshot is stale, fetch fresh instead.
      const useSnapshot = !marketChanged && !cartActivity && Array.isArray(config.cartItems);
      const hint = useSnapshot ? { items: config.cartItems, fromSnapshot: true } : null;
      scheduleSync(hint, { immediate: true });
    };

    if (marketChanged || isCartTemplate()) {
      run();
    } else if (typeof window.requestIdleCallback === 'function') {
      bootIdleHandle = window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      bootIdleHandle = setTimeout(run, 200);
    }
  }

  // Saturo: listen to the 4.1.1 standard cart event (replaces 'cart:update')
  document.addEventListener(StandardEvents.cartLinesUpdate, handleCartLinesUpdate);

  // Saturo (theme 5.91): a page restored from the back/forward cache (e.g.
  // back from checkout after removing a line there) does not run init()
  // again and its inline snapshot is stale - reconcile from a fresh fetch.
  // 5.180: Chrome never bfcaches product pages (Shop login iframe) and
  // rebuilds them from the HTTP disk cache instead: persisted is false, the
  // navigation type is back_forward, and init() ran on the same stale snapshot
  // (see component-cart-items.js #handlePageShow). That rerun also reruns the
  // market redirect, so it gets the same guard as start().
  window.addEventListener('pageshow', (event) => {
    const navigation = globalThis.performance?.getEntriesByType?.('navigation')[0];
    if (!event.persisted && navigation?.type !== 'back_forward') return;
    cartActivity = true;
    cancelBootIdle();
    const fallback = new Promise((resolve) => setTimeout(resolve, 6000, false));
    Promise.race([window.saturoMarketRedirect ?? false, fallback])
      .catch(() => false)
      .then((leaving) => leaving || scheduleSync(null, { immediate: true }));
  });

  // Saturo (theme 5.179): a root page being redirected into the visitor's market
  // (snippets/market-redirect.liquid) leaves the cart alone; the target page reconciles.
  // Two pages swapping deposits at once could leave both markets' deposits in the cart.
  function start() {
    const redirect = window.saturoMarketRedirect;
    if (!redirect) {
      init();
      return;
    }
    const fallback = new Promise((resolve) => setTimeout(resolve, 6000, false));
    Promise.race([redirect, fallback]).then((leaving) => leaving || init());
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Expose for debugging if needed
  window.DepositManager = {
    fetchCart,
    analyzeDeposits,
    scheduleSync,
    config
  };

})();
